import store from '@/store';
import { ACTION_SELECT_PLANT, STORE_LIBRARY_NAME } from '@/store_constants/library';
import {
	ACTION_GET_ALL_PERMISSIONS,
	GETTER_ALL_PERMISSIONS,
	GETTER_PERMISSIONS_BY_CATEGORY,
	STORE_PERMISSIONS_NAME
} from '@/store_constants/permissions';
import { checkUserPermissions } from '@/utils/userPermissionsControl';
import { ACTION_GET_USER_PERMISSIONS, GETTER_USER_PERMISSIONS, STORE_USER_NAME } from '@/store_constants/user';

export default async (to, from, next) => {
	const profile = store.state.user.profile;

	if (to.path.match(/^\/present/)) {
		return next();
	}

	// Save lastRoute
	if (to.path !== '/login' && to.path !== '/notfound' && to.path !== '/' && !to.path.match(/^\/present/)) {
		localStorage.setItem('lastPlantRoute', to.path);
	}

	// Log in to library if URL loaded is from another library
	if (to.params && to.params.library_id) {
		if (store.state[STORE_LIBRARY_NAME].plant_id !== to.params.library_id && profile) {
			await store.dispatch(`${STORE_LIBRARY_NAME}/${ACTION_SELECT_PLANT}`, to.params.library_id);
		}
	}

	if (to.redirectedFrom === '/') return next('/dashboard');
	if (to.path === '/login') return next();
	if (!profile) return next('/login');

	// Secure routes by user permissions
	await ensureUserPermissionsInitialized();

	const routesPermissions = getRoutesPermissions();

	const routeConfig = routesPermissions.find(route => route.name === to.name);

	if (routeConfig) {
		const routePermissions = Array.isArray(routeConfig.permissions[0])
			? routeConfig.permissions
			: [routeConfig.permissions];

		const isAllowed = routePermissions.some(permissions => checkUserPermissions(permissions));

		if (!isAllowed) return next({ name: routeConfig.redirect });
	}

	return next();
};

async function ensureUserPermissionsInitialized() {
	const getterAllPermissions = `${STORE_PERMISSIONS_NAME}/${GETTER_ALL_PERMISSIONS}`;
	const getterUserPermissions = `${STORE_PERMISSIONS_NAME}/${GETTER_USER_PERMISSIONS}`;

	const actionGetAllPermissions = `${STORE_PERMISSIONS_NAME}/${ACTION_GET_ALL_PERMISSIONS}`;
	const actionGetUserPermissions = `${STORE_USER_NAME}/${ACTION_GET_USER_PERMISSIONS}`;

	const allPermissions = store.getters[getterAllPermissions];
	const userPermissions = store.getters[getterUserPermissions];

	if (!allPermissions) await store.dispatch(actionGetAllPermissions);
	if (!userPermissions) await store.dispatch(actionGetUserPermissions);
}

function getRoutesPermissions() {
	const getterPermissionsByCategory = `${STORE_PERMISSIONS_NAME}/${GETTER_PERMISSIONS_BY_CATEGORY}`;
	const permissionsByCategory = store.getters[getterPermissionsByCategory];

	return [
		...getNotificationsRoutesPermissions(permissionsByCategory),
		...getWorkflowsRoutesPermissions(permissionsByCategory),
		...geContentRoutesPermissions(permissionsByCategory),
		...getConfigurationControlRoutesPermissions(permissionsByCategory),
		...getContentToolsRoutesPermissions(permissionsByCategory),
		...getBackgroundJobsRoutesPermissions(permissionsByCategory),
		...getBerthaJobsRoutesPermissions(permissionsByCategory),
		// ...getSemanticSearchRoutesPermissions(permissionsByCategory),
		...getRecycleBinRoutesPermissions(permissionsByCategory),
		...getSettingsRoutesPermissions(permissionsByCategory)
	];
}

function getNotificationsRoutesPermissions(permissionsByCategory) {
	return [
		{
			permissions: [permissionsByCategory.settings.receiveNotifications],
			name: 'Notifications',
			redirect: 'Dashboard'
		}
	];
}

function getWorkflowsRoutesPermissions(permissionsByCategory) {
	const workflowManagementPermissions = [
		permissionsByCategory.workflowManagement.editViewWorkflowDrafts,
		permissionsByCategory.workflowManagement.changeWorkflowType,
		permissionsByCategory.workflowManagement.reassignWorkflowRoles
	];

	return [
		{
			permissions: workflowManagementPermissions.map(permission => [permission]),
			name: 'Workflows',
			redirect: 'Dashboard'
		},
		{
			permissions: workflowManagementPermissions.map(permission => [permission]),
			name: 'Workflow',
			redirect: 'Dashboard'
		}
	];
}

function geContentRoutesPermissions(permissionsByCategory) {
	return [
		{
			permissions: [permissionsByCategory.workflowManagement.editViewWorkflowDrafts],
			name: 'Edit Draft',
			redirect: 'Dashboard'
		},
		{
			permissions: [permissionsByCategory.workflowManagement.editViewWorkflowDrafts],
			name: 'Preview Draft',
			redirect: 'Dashboard'
		},
		{
			permissions: [permissionsByCategory.workflowManagement.editViewWorkflowDrafts],
			name: 'Preview Activity',
			redirect: 'Dashboard'
		}
	];
}

function getConfigurationControlRoutesPermissions(permissionsByCategory) {
	return [
		{
			permissions: [permissionsByCategory.contentManagement.manageAdminTools],
			name: 'Configuration Control',
			redirect: 'Dashboard'
		}
	];
}

function getContentToolsRoutesPermissions(permissionsByCategory) {
	return [
		{
			permissions: [permissionsByCategory.contentManagement.manageAdminTools],
			name: 'Content Tools',
			redirect: 'Dashboard'
		},
		{
			permissions: [permissionsByCategory.contentManagement.manageAdminTools],
			name: 'Tools',
			redirect: 'Dashboard'
		}
	];
}

function getBackgroundJobsRoutesPermissions(permissionsByCategory) {
	return [
		{
			permissions: [permissionsByCategory.contentManagement.manageScorm],
			name: 'Background Jobs',
			redirect: 'Dashboard'
		}
	];
}

function getBerthaJobsRoutesPermissions(permissionsByCategory) {
	return [
		{
			permissions: [permissionsByCategory.contentManagement.manageAdminTools],
			name: 'Bertha Jobs',
			redirect: 'Dashboard'
		}
	];
}

//Need guard to access the route??
// function getSemanticSearchRoutesPermissions(permissionsByCategory) {
// 	return [
// 		{
// 			permissions: [permissionsByCategory.contentManagement.manageAdminTools],
// 			name: 'Semantic Search',
// 			redirect: 'Dashboard'
// 		}
// 	];
// }

function getRecycleBinRoutesPermissions(permissionsByCategory) {
	return [
		{
			permissions: [permissionsByCategory.contentManagement.manageAdminTools],
			name: 'Recycle bin',
			redirect: 'Dashboard'
		},
		{
			permissions: [permissionsByCategory.contentManagement.manageAdminTools],
			name: 'Library recycle bin',
			redirect: 'Dashboard'
		}
	];
}

function getSettingsRoutesPermissions(permissionsByCategory) {
	const childrenRoutes = [
		{
			permissions: [permissionsByCategory.settings.viewProfile],
			name: 'My Profile',
			redirect: 'Dashboard'
		},
		{
			permissions: [permissionsByCategory.settings.viewProfile],
			name: 'My Permissions',
			redirect: 'Dashboard'
		},
		{
			permissions: [permissionsByCategory.userManagement.userAccountManager],
			name: 'Site Users',
			redirect: 'Dashboard'
		},
		{
			permissions: [permissionsByCategory.userManagement.manageRoles],
			name: 'Site Roles',
			redirect: 'Dashboard'
		},
		{
			permissions: [permissionsByCategory.contentManagement.manageLibraries],
			name: 'Site Libraries',
			redirect: 'Dashboard'
		}
	];

	const parentRoute = {
		permissions: [],
		roles: [],
		name: 'Settings',
		redirect: getFirstAllowedRoute(childrenRoutes)
	};

	return [parentRoute, ...childrenRoutes];
}

function getFirstAllowedRoute(routes) {
	const firstAllowedRoute = routes.find(route => checkUserPermissions(route.permissions, route.roles));

	return firstAllowedRoute ? firstAllowedRoute.name : 'Dashboard';
}
